/**
 * "Any thoughts?" — the reader's seed notes, in a panel of their own.
 *
 * Whatever they type rides into the generation prompt as passage-gated
 * observations (and later onto the study + printout). It sits beside the
 * passage's In Context card rather than inside it, and carries the accuracy
 * checklist's panel shape so the two read as the same kind of thing: a
 * foldable box of work, one the reader's, one the model's.
 *
 * Collapsed by default. Call sites that want it inviting (the pericope study
 * card opens it before generation, then folds it once commentary lands —
 * never hides it) pass `open`; the reader's own toggle always wins until the
 * hint changes again.
 */

import { useEffect, useRef, useState } from 'react';
import { ChevronRightIcon } from '@components/icons';

const MAX_THOUGHTS_LENGTH = 2000;

interface ThoughtsAccordionProps {
  value: string;
  onChange: (value: string) => void;
  /** Expansion hint: the initial state, and the state it snaps to each time
   *  this value changes. Omit for a plain collapsed-by-default panel. */
  open?: boolean;
}

function wordCount(value: string): number {
  const trimmed = value.trim();
  return trimmed ? trimmed.split(/\s+/).length : 0;
}

export function ThoughtsAccordion({ value, onChange, open: openHint }: ThoughtsAccordionProps) {
  const [open, setOpen] = useState(openHint ?? false);
  // Re-sync only when the hint itself flips, so a manual toggle survives
  // every unrelated re-render of the card.
  const lastHint = useRef(openHint);
  useEffect(() => {
    if (openHint === undefined || openHint === lastHint.current) return;
    lastHint.current = openHint;
    setOpen(openHint);
  }, [openHint]);

  const words = wordCount(value);

  return (
    <div
      className={`thoughts-panel${open ? '' : ' thoughts-panel--collapsed'}`}
      data-testid="thoughts-accordion"
    >
      <button
        type="button"
        className="thoughts-panel-toggle"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        title={open ? 'Hide your thoughts' : 'Show your thoughts'}
        data-testid="thoughts-toggle"
      >
        <ChevronRightIcon
          className={`thoughts-panel-chevron h-3.5 w-3.5${open ? ' open' : ''}`}
        />
        <span className="thoughts-panel-title">Any thoughts?</span>
        {!open && words > 0 && (
          <span className="thoughts-panel-count">
            {words} word{words === 1 ? '' : 's'}
          </span>
        )}
      </button>
      <div className="thoughts-panel-body">
        <p className="thoughts-panel-note">
          Experimental — your notes seed the AI commentary and can skew its
          results. The AI develops them only where the passage supports them.
        </p>
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value.slice(0, MAX_THOUGHTS_LENGTH))}
          placeholder="What did you see in this passage?"
          rows={3}
          className="thoughts-panel-input"
          // The body stays mounted so it can animate; a collapsed panel must
          // still not catch a Tab.
          tabIndex={open ? 0 : -1}
          data-testid="thoughts-input"
        />
      </div>
    </div>
  );
}
